import { defineStore } from 'pinia'
import { ref } from 'vue'

import type {
	LastPrinted,
	PrintReport,
	TrainingLog,
	TrainingMember,
	URLParams,
} from '@vuejs/services/trainingLog/interfaces'

import type { Course } from '../services/goals/interfaces'
import { useGlobalStore } from './GlobalStore'
import { useMemberTrainingStore } from './MemberTrainingStore'

export interface TrainingLogOptions {
	isActive: boolean
	isShown: boolean
	label: string
}

export interface TrainingLogPaths {
	lastPrinted: string
	training: string
}

export const useTrainingLogStore = defineStore('trainingLog', () => {
	// State
	const agencyId = ref<number | null>(null)
	const endDate = ref('')
	const endDateFormattedString = ref('')
	const franchisorId = ref<number | null>(null)
	const globalStore = useGlobalStore()
	const hasErrors = ref(false)
	const hiddenCourseIds = ref<string[]>([])
	const isForSpecificClass = ref(false)
	const lastPrinted = ref<LastPrinted>({
		certificate: '',
		report: '',
	})
	const loading = ref(true)
	const members = ref<TrainingMember[]>([])
	const options = ref<TrainingLogOptions[]>([
		{
			isActive: true,
			isShown: true,
			label: 'Descriptions',
		},
		{
			isActive: false,
			isShown: true,
			label: 'State Approvals',
		},
		{
			isActive: false,
			isShown: true,
			label: 'Learning Objectives',
		},
		{
			isActive: false,
			isShown: true,
			label: 'Scores',
		},
		{
			isActive: true,
			isShown: true,
			label: 'Waived Date',
		},
	])
	const paths = ref<TrainingLogPaths>({
		lastPrinted: '',
		training: '',
	})
	const printReport = ref<PrintReport>({
		courseStatus: '',
		dateChoice: 'since_last_printed',
		emittedEvent: '',
		groupId: '',
		lastPrintedAt: '',
		memberId: '',
		sinceAccountCreated: '',
		title: '',
		url: '',
	})
	const showIncomplete = ref(false)
	const showOptions = ref(false)
	const showSampleData = ref(false)
	const specificClasses = ref<Course[]>([])
	const startDate = ref('')
	const startDateFormattedString = ref('')
	const title = ref('')
	const trainingLog = ref<TrainingLog>({} as TrainingLog)
	const updateLastPrintedAt = ref('false')
	const urlParams = ref<URLParams>({
		caregiver_id: '',
		course_ids: [],
		date_choices: '',
		franchisor_id: '',
		hidden_caregiver_group_id: '',
		hidden_caregiver_id: '',
		hidden_course_status: '',
		hidden_parsed_end_date: '',
		hidden_parsed_start_date: '',
	})

	// Actions
	const handleClosePrintReport = () => {
		const memberTrainingStore = useMemberTrainingStore()

		memberTrainingStore.selectedCourseIds = []
		globalStore.resetCAModal()
		printReport.value = {
			courseStatus: '',
			dateChoice: 'since_last_printed',
			emittedEvent: '',
			groupId: '',
			lastPrintedAt: '',
			memberId: '',
			sinceAccountCreated: '',
			title: '',
			url: '',
		}
	}

	const handleOpenPrintReport = () => {
		const memberTrainingStore = useMemberTrainingStore()
		const courseIdsParamString = memberTrainingStore.selectedCourseIds.join('&course_ids=')

		const params = `
			?hidden-caregiver-id=${printReport.value.memberId ?? ''}
			&date-choices=${printReport.value.dateChoice}
			&custom-range-start=${printReport.value.customRangeStart ?? ''}
			&hidden-start-date-parsed=${printReport.value.customRangeStartDate ?? ''}
			&custom-range-end=${printReport.value.customRangeEnd ?? ''}
			&hidden-end-date-parsed=${printReport.value.customRangeEndDate ?? ''}
			&hidden-course-search-type=${printReport.value.courseSearchType ?? ''}
			&hidden-course-status=${printReport.value.courseStatus}
			&hidden-caregiver-group-id=${printReport.value.groupId ?? ''}
			&commit=Print+report
		`

		const url = globalStore.emittedEvent === 'print-reports' ?
			`${printReport.value.url}${params}` :
			`${printReport.value.url}?hidden-course-status=${printReport.value.courseStatus ?? ''}&course_ids=${courseIdsParamString}`

		window.open(url)
		handleClosePrintReport()
	}

	const setPrintReport = (report: PrintReport) => {
		printReport.value = report
		globalStore.emittedEvent = report.emittedEvent ?? ''
	}

	return {
		// State
		agencyId,
		endDate,
		endDateFormattedString,
		franchisorId,
		handleClosePrintReport,
		handleOpenPrintReport,
		hasErrors,
		hiddenCourseIds,
		isForSpecificClass,
		lastPrinted,
		loading,
		members,
		options,
		paths,
		printReport,
		setPrintReport,
		showIncomplete,
		showOptions,
		showSampleData,
		specificClasses,
		startDate,
		startDateFormattedString,
		title,
		trainingLog,
		updateLastPrintedAt,
		urlParams,
	}
})
